import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { API_PATH } from 'src/environments/environment';
import { BARID_LOCAL_AGENT_TICKET_RENEW_BEFORE_MS, BaridLocalAgentTicketIssueResponse } from './barid-contracts';

/** Computador para o qual o ticket e pedido (machineId do `GET /status` do agente). */
export interface BaridTicketScope {
  machineId: string;
  /** Device pareado neste computador (`pairingProfile.deviceSettingId`); opcional na API. */
  deviceId?: string;
}

/** Ticket pronto para o loopback: vai no `X-Barid-Agent-Ticket` junto do `x-tenant-id`. */
export interface BaridTicketGrant {
  ticket: string;
  tenantId: string;
}

/** Abaixo disto o ticket ja nao e usado (relogio do computador x nuvem). */
export const BARID_TICKET_MIN_REMAINING_MS = 60_000;
/** Sem ticket por 404 (computador nao pareado nesta empresa / API sem a rota): nao insiste por 5 min. */
export const BARID_TICKET_NOT_FOUND_BACKOFF_MS = 5 * 60_000;
/** Demais falhas (401, rede, 5xx): nao insiste por 1 min. */
export const BARID_TICKET_FAILURE_BACKOFF_MS = 60_000;

interface CachedTicket {
  key: string;
  ticket: string;
  tenantId: string;
  issuedAtMs: number;
  expiresAtMs: number;
}

/**
 * Ticket do agente local (contrato 1.30.0, "uma vez pareado, funciona de
 * qualquer navegador neste terminal"). A nuvem emite para ESTE computador e
 * para o tenant da sessao; o agente valida localmente.
 *
 * - So em MEMORIA (nunca localStorage) e nunca logado.
 * - Renovado quando faltar menos de 1 h (ou metade da validade) para expirar.
 * - Falha da nuvem (404 = computador nao pareado nesta empresa ou API sem a
 *   rota; 401; rede) devolve `null`: o cliente segue com o Bearer do
 *   pareamento deste navegador, como antes.
 *
 * Usa HttpBackend (sem o AuthInterceptor) de proposito: o interceptor
 * transforma o erro em texto (perde o status) e mostra aviso global, e aqui
 * falha e caminho normal (agente antigo, API sem a rota, outro tenant).
 */
@Injectable({ providedIn: 'root' })
export class BaridLocalTicketService {
  /** Sobrescrito nos testes. */
  now: () => number = () => Date.now();

  private readonly http: HttpClient;
  private cached: CachedTicket | null = null;
  private inflight: { key: string; promise: Promise<CachedTicket | null> } | null = null;
  private unavailable: { key: string; until: number } | null = null;
  /**
   * Geracao do cache: `invalidate()` incrementa. Uma ida a nuvem iniciada numa geracao anterior
   * (em voo durante o invalidate) descarta o resultado: nunca regrava `cached` nem marca
   * indisponivel depois de o ticket ter sido esquecido (recusa do agente, desparear, troca de sessao).
   */
  private generation = 0;

  constructor(httpBackend: HttpBackend, private readonly auth: AuthService) {
    this.http = new HttpClient(httpBackend);
  }

  /** Ha ticket valido em memoria para este computador (sem ir a nuvem). */
  hasValidTicket(scope?: BaridTicketScope | null): boolean {
    const cached = this.cached;
    if (!cached || cached.expiresAtMs - this.now() <= BARID_TICKET_MIN_REMAINING_MS) return false;
    if (!scope) return true;
    const key = this.scopeKey(scope);
    return key !== null && key === cached.key;
  }

  /**
   * Ticket para o loopback, ou `null` quando a nuvem nao emite (o chamador
   * cai no Bearer do pareamento). Nunca rejeita.
   */
  async getTicket(scope: BaridTicketScope, options: { forceRefresh?: boolean } = {}): Promise<BaridTicketGrant | null> {
    const key = this.scopeKey(scope);
    if (!key) return null;
    const now = this.now();

    if (this.cached && this.cached.key !== key) this.cached = null;
    if (options.forceRefresh) {
      this.cached = null;
      this.unavailable = null;
    }

    const cached = this.cached;
    if (cached && !this.shouldRenew(cached, now)) return this.grant(cached);

    if (this.unavailable && this.unavailable.key === key && this.unavailable.until > now) {
      return cached && this.usable(cached, now) ? this.grant(cached) : null;
    }

    const issued = await this.issue(key, scope);
    if (issued) return this.grant(issued);
    // Renovacao falhou: o ticket atual ainda serve ate perto de expirar.
    const current = this.cached;
    return current && current.key === key && this.usable(current, this.now()) ? this.grant(current) : null;
  }

  /** Esquece o ticket (recusado pelo agente, re-pareamento, desparear, troca de sessao). */
  invalidate(): void {
    this.generation += 1;
    this.cached = null;
    this.unavailable = null;
    this.inflight = null;
  }

  private shouldRenew(cached: CachedTicket, now: number): boolean {
    const lifetime = Math.max(0, cached.expiresAtMs - cached.issuedAtMs);
    const renewBefore = Math.min(BARID_LOCAL_AGENT_TICKET_RENEW_BEFORE_MS, lifetime / 2);
    return cached.expiresAtMs - now <= Math.max(renewBefore, BARID_TICKET_MIN_REMAINING_MS);
  }

  private usable(cached: CachedTicket, now: number): boolean {
    return cached.expiresAtMs - now > BARID_TICKET_MIN_REMAINING_MS;
  }

  private grant(cached: CachedTicket): BaridTicketGrant {
    return { ticket: cached.ticket, tenantId: cached.tenantId };
  }

  /** Tenant + usuario da sessao + computador: ticket de outra sessao/computador nunca e reaproveitado. */
  private scopeKey(scope: BaridTicketScope): string | null {
    const tenantId = this.auth.getTenantId();
    const jwt = this.auth.getAuthorizationToken();
    const machineId = scope?.machineId?.trim();
    if (!tenantId || !jwt || !machineId) return null;
    return [tenantId, this.sessionFingerprint(jwt), machineId, scope.deviceId?.trim() || ''].join('|');
  }

  /** Identifica a sessao sem guardar o JWT na chave (so o sujeito, se legivel). */
  private sessionFingerprint(jwt: string): string {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return String(payload?.tenantUserId ?? payload?.sub ?? payload?.id ?? '');
    } catch {
      return '';
    }
  }

  private issue(key: string, scope: BaridTicketScope): Promise<CachedTicket | null> {
    if (this.inflight && this.inflight.key === key) return this.inflight.promise;
    const promise = this.request(key, scope).finally(() => {
      if (this.inflight?.promise === promise) this.inflight = null;
    });
    this.inflight = { key, promise };
    return promise;
  }

  private async request(key: string, scope: BaridTicketScope): Promise<CachedTicket | null> {
    const generation = this.generation;
    const tenantId = this.auth.getTenantId();
    const jwt = this.auth.getAuthorizationToken();
    if (!tenantId || !jwt || this.auth.isTokenExpired(jwt)) return null;

    const body: { machineId: string; deviceId?: string } = { machineId: scope.machineId.trim() };
    if (scope.deviceId?.trim()) body.deviceId = scope.deviceId.trim();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${jwt}`,
      'x-tenant-id': tenantId,
      'Content-Type': 'application/json'
    });

    try {
      const response = await firstValueFrom(
        this.http.post<BaridLocalAgentTicketIssueResponse>(`${API_PATH}barid-devices/local-agent-ticket`, body, { headers })
      );
      // Invalidado durante a ida a nuvem: descarta (nao regrava o cache esquecido nem marca indisponivel).
      if (generation !== this.generation) return null;
      const issuedAtMs = this.now();
      const expiresAtMs = Date.parse(response?.expiresAt ?? '');
      if (typeof response?.ticket !== 'string' || !response.ticket || !Number.isFinite(expiresAtMs) || expiresAtMs <= issuedAtMs) {
        this.markUnavailable(key, BARID_TICKET_FAILURE_BACKOFF_MS, 'resposta inesperada');
        return null;
      }
      // A sessao pode ter mudado durante a ida a nuvem: so guarda se o escopo ainda e o mesmo.
      if (this.scopeKey(scope) !== key) return null;
      const cached: CachedTicket = { key, ticket: response.ticket, tenantId, issuedAtMs, expiresAtMs };
      this.cached = cached;
      this.unavailable = null;
      return cached;
    } catch (error) {
      if (generation !== this.generation) return null;
      const status = error instanceof HttpErrorResponse ? error.status : undefined;
      this.markUnavailable(
        key,
        status === 404 ? BARID_TICKET_NOT_FOUND_BACKOFF_MS : BARID_TICKET_FAILURE_BACKOFF_MS,
        status ? `HTTP ${status}` : 'sem resposta'
      );
      return null;
    }
  }

  private markUnavailable(key: string, backoffMs: number, reason: string): void {
    this.unavailable = { key, until: this.now() + backoffMs };
    // Sem segredo no log: nem ticket, nem JWT, nem corpo.
    console.debug(`[Barid] ticket do agente local indisponível (${reason}); seguindo com o pareamento deste navegador.`);
  }
}
